import {
  ComcastErrorType,
  platformStoreDelete,
  platformStoreGet,
  triggerSentryError,
} from '../../../../shared/helpers.js'
import { getUserStation } from '../../actions/contentActions.js'
import {
  deActivateDevice,
  getActivationStatus,
} from '../../actions/profileActions'
import { getStationDonationAmountsHelper } from '../../actions/tvaultActions.js'
import { registerUser } from '../../actions/userActions.js'
import { ExtractedStation } from '../../lib/utils/station/stationUtils'
import { store } from '../../store.js'
import ActivationRequiredError from './ActivationRequiredError'
import ActivationServiceError from './ActivationServiceError'

declare const COMPILING_FOR_COMCASTX1: boolean
declare const COMCAST_IN_APP_DONATIONS_ENABLED: boolean

const ERROR_CODE_MULTIPLE_USERS_ASSIGNED = 500101
const ERROR_CODE_NO_USER_ASSOCIATED = 500102

const UID_STORAGE_KEY = 'uID-ga'
const DEVICE_STORAGE_KEY = 'deviceId-ga'

function getStoredUid(): string | null {
  return platformStoreGet(UID_STORAGE_KEY)
}

function getStoredDeviceId(): string | null {
  return platformStoreGet(DEVICE_STORAGE_KEY)
}

function resetActivationData(): void {
  platformStoreDelete(UID_STORAGE_KEY)
  platformStoreDelete(DEVICE_STORAGE_KEY)
  store.dispatch({
    type: 'UPDATE_DEVICE',
    payload: { UID: null },
  })
}

async function deactivateDeviceAndCleanup(): Promise<void> {
  const storedUid = getStoredUid()
  const storedDeviceId = getStoredDeviceId()
  if (storedUid && storedDeviceId) {
    try {
      await deActivateDevice()
      resetActivationData()
    } catch (error) {
      throw new ActivationServiceError(
        'Deactivation failed',
        'deactivate',
        error
      )
    }
  }
}

async function getStationInformation(
  uid: string
): Promise<ExtractedStation | undefined> {
  try {
    return await getUserStation(uid)
  } catch (error) {
    throw new ActivationServiceError(
      'Failed to load station data',
      'getUserStation',
      error
    )
  }
}

async function registerUserInApplication(uid: string): Promise<void> {
  try {
    await registerUser(uid)
  } catch (error) {
    throw new ActivationServiceError(
      'Failed to register user',
      'registerUser',
      error
    )
  }
}

async function initializeUserData(uid: string): Promise<void> {
  const stationData = await getStationInformation(uid)
  await registerUserInApplication(uid)

  if (!stationData) {
    return
  }

  store.dispatch({
    type: 'UPDATE_STATION',
    payload: stationData,
  })

  store.dispatch({
    type: 'SET_HOME_CONTENT',
    payload: { collections: null },
  })

  if (COMPILING_FOR_COMCASTX1 && COMCAST_IN_APP_DONATIONS_ENABLED) {
    getStationDonationAmountsHelper(stationData.call_sign)
  }
}

function extractProfileServiceError(error: any) {
  try {
    const response = error?.response

    if (typeof response === 'string') {
      return JSON.parse(response)
    }
    return response ?? error
  } catch (parseError) {
    return {
      message: "We're unable to start the app due to an unexpected error.",
      errorCode: 'PARSE_ERROR',
      parseError,
    }
  }
}

function sendErrorToSentry(
  error: unknown,
  {
    sentryMessage,
    errorMessage,
    errorLocation,
    errorType = ComcastErrorType.restriction,
  }: {
    sentryMessage: string
    errorMessage?: string
    errorLocation: string
    errorType?: string
  }
) {
  const metadata: Record<string, unknown> = {
    errorLocation,
    errorType,
    rawError: error,
  }

  if (errorMessage) {
    metadata.errorMessage = errorMessage
  }

  triggerSentryError(sentryMessage, 'CheckUserActivation', metadata, true)
}

export default async function initActivationVerification() {
  const storedUid = getStoredUid()
  try {
    const resp = await getActivationStatus()
    const uid = resp?.device?.UID

    if (!uid) {
      throw new ActivationRequiredError('no_uid')
    }

    if (storedUid !== uid) {
      await deactivateDeviceAndCleanup()
      throw new ActivationRequiredError('uid_mismatch')
    }

    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: { UID: uid },
    })

    await initializeUserData(uid)
  } catch (error) {
    if (
      error instanceof ActivationRequiredError ||
      error instanceof ActivationServiceError
    ) {
      sendErrorToSentry(error, {
        sentryMessage: `Activation Error: ${error.message}`,
        errorLocation: 'CheckUserActivation - initActivationVerification',
      })
      throw error
    }

    const profileServiceError = extractProfileServiceError(error)

    if (profileServiceError?.errorCode === ERROR_CODE_MULTIPLE_USERS_ASSIGNED) {
      await deactivateDeviceAndCleanup()
      throw new ActivationRequiredError('multiple_users')
    }

    if (profileServiceError?.errorCode === ERROR_CODE_NO_USER_ASSOCIATED) {
      await deactivateDeviceAndCleanup()
      throw new ActivationRequiredError('no_user_associated')
    }

    sendErrorToSentry(error, {
      sentryMessage: `csError.errorCode - ${profileServiceError?.errorCode}`,
      errorLocation: 'CheckUserActivation - initActivationVerification',
    })

    if (profileServiceError?.errorCode === 'PARSE_ERROR') {
      throw new ActivationServiceError(
        '',
        'matchmaker error',
        profileServiceError
      )
    }

    throw new ActivationServiceError('', 'csjsonerror', profileServiceError)
  }
}
